import { useSettingsStore } from "@context/useSettingsStore";
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { getAssignationTypesByLink } from "../graphql/assignationTypes.queries";
import { AssignationTypeFilterInput, AssignationTypePage } from "@models/ISchema";
import { getLinkBlockingLimitStatus } from "@utils/assignationLimits";

type AssignationTypeParams = {
  originId: string | number;
  scenarioId: string | number;
  linkId: string | number;
  courseType?: string | null;
  /** Fetch only while editing an eligible link, so the limit status is known before the selector opens. */
  shouldFetch: boolean;
};

/**
 * Loads the assignation types of a link with their limit (`maxAssignmentsByLink`)
 * and `limitStatus`. The default `no-cache` fetch policy is intentional: the same
 * assignation type id carries different per-link values, so a normalized cache
 * would let links overwrite each other.
 */
export const useGetAssignationsTypesByLink = ({
  originId,
  scenarioId,
  linkId,
  courseType,
  shouldFetch,
}: AssignationTypeParams) => {
  const adaptedAssignmentsByCourseType = useSettingsStore(
    state => state.adaptedAssignmentsByCourseType,
  );

  const { data: rawAssignations, loading } = useQuery<
    {
      data: {
        assignationTypes: AssignationTypePage;
      };
    },
    {
      originId: string | number;
      scenarioId: string | number;
      filter: AssignationTypeFilterInput;
    }
  >(getAssignationTypesByLink, {
    skip: !shouldFetch,
    variables: {
      originId,
      scenarioId,
      filter: {
        fields: {
          linkId: {
            eq: Number(linkId),
          },
        },
      },
    },
  });

  // Filter assignation types that exist in adaptedAssignments
  const options = useMemo(() => {
    if (!rawAssignations || !courseType) return [];

    /**
     * Important: Course type link should always be in lowercase to match the adaptedAssignmentsByCourseType
     */
    const courseTypeLink = courseType.toLowerCase();
    const courseTypeAssignments = adaptedAssignmentsByCourseType?.[courseTypeLink] || {};

    return (
      rawAssignations?.data?.assignationTypes?.items
        ?.filter(
          assignation =>
            courseTypeAssignments && !!courseTypeAssignments?.[assignation?.code?.toLowerCase()],
        )
        ?.map(assignation => ({
          label: assignation.name,
          value: assignation.id,
          data: {
            ...assignation,
            courseType:
              courseTypeAssignments?.[assignation.code?.toLowerCase()]?.course_type_link || "",
          },
        })) ?? []
    );
  }, [rawAssignations, adaptedAssignmentsByCourseType, courseType]);

  const blockingLimitStatus = useMemo(
    () => getLinkBlockingLimitStatus(options.map(option => option.data)),
    [options],
  );

  return {
    options,
    blockingLimitStatus,
    isLoading: loading,
  };
};
